import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Server, Cloud, Network, Lock, Briefcase, 
  Phone, CheckCircle, Clock, Award, Users, Building2, Scale,
  ChevronRight, Star, Target
} from 'lucide-react';
import SimpleContactForm from '../components/Forms/SimpleContactForm';

const HomePage = () => {
  const services = [
    {
      icon: Server,
      title: 'Managed IT Services',
      description: 'Network design, server administration, virtualization, and 24/7 remote monitoring. Let us be your complete outsourced IT department.',
      link: '/services#managed-services'
    },
    {
      icon: Cloud,
      title: 'Cloud & SaaS Management',
      description: 'AWS, Azure, Google Cloud integration. Microsoft 365 administration, cloud backup, and disaster recovery solutions.',
      link: '/services#cloud-services'
    },
    {
      icon: Shield,
      title: 'Cybersecurity & Compliance',
      description: 'Endpoint security, SIEM, threat intelligence, and compliance adherence (HIPAA, GDPR, SOC 2, PCI-DSS, NIST, ISO 27001).',
      link: '/services#compliance'
    },
    {
      icon: Briefcase,
      title: 'vCTO / vCISO Advisory',
      description: 'Executive-level technology and security strategy consulting on a flexible, part-time basis.',
      link: '/services#fractional-cto'
    },
    {
      icon: Lock,
      title: 'Physical Security Systems',
      description: 'Video surveillance, access control, intrusion alarms, and environmental sensors — fully integrated with your IT.',
      link: '/services#physical-security'
    },
    {
      icon: Users,
      title: 'VoIP & Unified Communications',
      description: 'VoIP systems, Microsoft Teams, Zoom administration, PBX deployment, and audio-visual solutions.',
      link: '/services#voip'
    },
  ];

  const whyChooseUs = [
    {
      icon: Target,
      title: 'White-Glove Service',
      description: 'Personal attention and responsive support. Every client receives relationship-driven service that ensures your needs are heard and addressed promptly.'
    },
    {
      icon: Award,
      title: 'Enterprise-Grade Solutions',
      description: 'Despite our boutique approach, we deliver the same sophisticated infrastructure found in large-scale environments—from cloud computing to physical security.'
    },
    {
      icon: Shield,
      title: 'Deep Expertise',
      description: 'Proven track record managing complex, multi-site environments and securing public infrastructure across industries.'
    },
    {
      icon: Building2,
      title: 'Licensed & Certified',
      description: 'NY & NJ licensed for alarms and telecommunications, with industry certifications and MBE/SBE status.'
    },
  ];

  const industries = [
    { name: 'Law Firms', icon: Scale },
    { name: 'Property Management', icon: Building2 },
    { name: 'Multi-Site Businesses', icon: Network },
    { name: 'Regulated Industries', icon: Shield },
  ];

  const testimonials = [
    {
      quote: "mimoIT transformed our IT infrastructure. Their proactive approach has virtually eliminated our downtime.",
      author: "Sarah J.",
      role: "Managing Partner",
      company: "Law Firm, Manhattan"
    },
    {
      quote: "Finally, an IT partner that understands both technology and business. Their vCIO services have been invaluable.",
      author: "Michael R.",
      role: "CEO",
      company: "Property Management, NJ"
    },
    {
      quote: "The security improvements mimoIT implemented gave us the confidence to pursue larger contracts.",
      author: "David L.",
      role: "Operations Director",
      company: "Healthcare Services, NY"
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-mimo-primary/90 to-slate-900/80" />
        
        <div className="container-custom relative z-10 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Shield className="w-4 h-4 text-mimo-secondary" />
                <span className="text-sm font-medium">Trusted IT Partner for NJ & NY Businesses</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold font-heading leading-tight mb-6">
                Enterprise IT Solutions.
                <span className="block text-mimo-secondary">SMB Pricing.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed max-w-xl">
                Enterprise IT, cybersecurity, and physical security solutions with personal, relationship-driven service. We deliver sophisticated technology infrastructure tailored to your business—from cloud computing to endpoint protection and surveillance systems.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to="/contact" 
                  className="btn-secondary flex items-center justify-center gap-2"
                  data-testid="hero-cta-consultation"
                >
                  Request Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="tel:201-473-4443" 
                  className="border-2 border-white/30 hover:border-white hover:bg-white/10 text-white rounded-full px-8 py-3 font-semibold transition-all flex items-center justify-center gap-2"
                  data-testid="hero-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  Call 201-473-4443
                </a>
              </div>
              
              <div className="flex flex-wrap gap-6 text-sm text-slate-300">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>24/7 Monitoring</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>NIST Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Certified MBE</span>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block animate-slide-in-right animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-mimo-secondary/20 to-mimo-accent/20 rounded-3xl blur-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=800" 
                  alt="IT professionals working" 
                  className="relative rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-mimo-surface py-8 border-b border-slate-200">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold font-heading text-mimo-primary">25+</p>
              <p className="text-sm text-slate-600">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold font-heading text-mimo-primary">98%</p>
              <p className="text-sm text-slate-600">Client Retention</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold font-heading text-mimo-primary">24/7/365</p>
              <p className="text-sm text-slate-600">Monitoring</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-heading text-mimo-primary">Enterprise</p>
              <p className="text-sm text-slate-600">Grade Security</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold font-heading text-mimo-primary">Fully Integrated</p>
              <p className="text-sm text-slate-600">Infrastructure</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold font-heading text-mimo-primary">10+</p>
              <p className="text-sm text-slate-600">Avg Client Tenure</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Comprehensive IT Solutions
            </h2>
            <p className="text-lg text-slate-600">
              From day-to-day IT support to strategic cybersecurity initiatives, we provide end-to-end technology services tailored to your business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link 
                key={service.title}
                to={service.link}
                className="card-hover group"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-mimo-primary/10 flex items-center justify-center mb-6 group-hover:bg-mimo-primary group-hover:scale-110 transition-all duration-300">
                  <service.icon className="w-7 h-7 text-mimo-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-3 group-hover:text-mimo-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {service.description}
                </p>
                <span className="inline-flex items-center text-mimo-primary font-medium group-hover:gap-2 transition-all">
                  Learn More <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-outline" data-testid="view-all-services">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Information Security Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Information Security — Beyond Just IT
            </h2>
            <p className="text-xl text-mimo-primary font-semibold mb-4">
              Cybersecurity + Physical Security = Complete Protection
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              True information security requires more than just antivirus and firewalls. Your business is protected only when both your digital systems and physical infrastructure work together securely.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
            {/* Image as Visual Aid */}
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_mimoit-refresh/artifacts/wnntdond_Cyber_Phys-InfoSec.JPG" 
                alt="Information Security - Cybersecurity and Physical Security Integration" 
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2 space-y-8">
              {/* Cybersecurity */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-8 h-8 text-mimo-primary" />
                  <h3 className="text-2xl font-bold font-heading text-slate-900">Cybersecurity</h3>
                </div>
                <p className="text-slate-600 mb-4">Protecting your networks, devices, and data</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Advanced firewall & network security</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Endpoint protection & threat monitoring</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Microsoft 365 & cloud security hardening</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Data backup & ransomware protection</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>24/7 monitoring & incident response</span>
                  </li>
                </ul>
              </div>
              
              {/* Physical Security */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-8 h-8 text-mimo-primary" />
                  <h3 className="text-2xl font-bold font-heading text-slate-900">Physical Security</h3>
                </div>
                <p className="text-slate-600 mb-4">Protecting your facilities, equipment, and access</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Enterprise-grade CCTV systems</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Access control & door entry systems</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Secure server room protection</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Multi-site surveillance management</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-mimo-secondary flex-shrink-0 mt-0.5" />
                    <span>Remote access & monitoring solutions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Why Integration Matters */}
          <div className="bg-gradient-to-br from-mimo-primary to-mimo-accent rounded-2xl p-8 text-center">
            <h3 className="text-xl font-bold font-heading text-white mb-4">Why Integration Matters</h3>
            <p className="text-white mb-4 max-w-2xl mx-auto">
              Many providers handle IT. Others handle cameras and access control. <span className="font-semibold">We integrate both.</span>
            </p>
            <p className="text-white/80 text-sm max-w-2xl mx-auto">
              If your server room is unlocked, your cybersecurity is exposed. If your network is insecure, your cameras and access systems are vulnerable. mimoIT designs security environments where cyber and physical systems operate as one unified defense strategy.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Why Businesses Trust <span className="text-mimo-primary">mimoIT</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Your strategic technology partner committed to delivering enterprise solutions that fit your budget and drive your success.
              </p>
              
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={item.title} className="flex gap-4" data-testid={`why-choose-${index}`}>
                    <div className="w-12 h-12 rounded-lg bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-mimo-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold font-heading text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-mimo-secondary/10 to-mimo-accent/10 rounded-3xl blur-xl" />
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=800" 
                alt="Professional IT team" 
                className="relative rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Certified & Licensed</p>
                    <p className="text-sm text-slate-600">Tri-State Security Licenses</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-slate-600">
              Specialized IT expertise for businesses that demand reliability, security, and compliance.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry) => (
              <Link 
                key={industry.name}
                to="/industries"
                className="bg-slate-50 hover:bg-mimo-primary group rounded-xl p-8 text-center transition-all duration-300"
              >
                <industry.icon className="w-10 h-10 text-mimo-primary group-hover:text-white mx-auto mb-4 transition-colors" />
                <h3 className="font-bold font-heading text-slate-900 group-hover:text-white transition-colors">
                  {industry.name}
                </h3>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/industries" className="text-mimo-primary font-medium hover:underline inline-flex items-center gap-1">
              See all industries <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-br from-mimo-primary to-mimo-accent text-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
              Trusted by Businesses Across NJ & NY
            </h2>
            <p className="text-white/80 text-lg">
              See what our clients say about working with mimoIT
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold">{testimonial.author}</p>
                  <p className="text-white/70 text-sm">{testimonial.role}</p>
                  <p className="text-white/70 text-sm">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact Form */}
      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-slate-900 mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Let's discuss how mimoIT can help secure and optimize your IT infrastructure. Fill out the form and we'll get back to you within 24 hours.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-mimo-secondary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Call Us</h4>
                    <a href="tel:201-473-4443" className="text-mimo-primary hover:underline">201-473-4443</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-mimo-secondary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">24/7 Support</h4>
                    <p className="text-slate-600">Emergency support always available</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-mimo-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-mimo-secondary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Free IT Assessment</h4>
                    <p className="text-slate-600">No obligation evaluation of your current setup</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold font-heading text-slate-900 mb-6">Send Us a Message</h3>
              <SimpleContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;